"use client";

import { useLocale } from "@i18n/react";
import { useSearch } from "@packages/framework";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useRequestMutation } from "../../../../../request/hooks/useRequestMutation";
import { useRequestQuery } from "../../../../../request/hooks/useRequestQuery";
import { keyCommunityAssignments } from "../keyCommunityAssignments";
import { communityAssignmentSchema } from "../schema/communityAssignmentSchema";
import { keyCommunityAssignment } from "./keyCommunityAssignment";

type UseRequestCommunityAssignmentProps = {
  assignmentId: number;
  enabled?: boolean;
  classId: number;
};

export const useRequestCommunityAssignment = ({
  assignmentId,
  classId,
  enabled = true,
}: UseRequestCommunityAssignmentProps) => {
  const locale = useLocale();
  const queryClient = useQueryClient();
  const search = useSearch();

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [
        keyCommunityAssignment({ assignmentId, classId: classId }),
        locale,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        keyCommunityAssignments({
          id: classId,
          search: search.get("search") ?? "",
        }),
        locale,
      ],
    });
  }, [assignmentId, classId, locale, queryClient, search]);

  return {
    query: useRequestQuery({
      enabled,
      queryFnRequestProps: {
        schema: communityAssignmentSchema,
        urlBackendPath: `/communities/${classId}/admin/assignments/${assignmentId}`,
      },
      queryKey: keyCommunityAssignment({ assignmentId, classId }),
    }),
    mutationDelete: useRequestMutation<any, void>({
      mutationFnRequestProps: () => ({
        urlBackendPath: `/communities/${classId}/admin/assignments/${assignmentId}`,
        method: "DELETE",
      }),
      onSuccess: () => invalidateQueries(),
    }),
    mutationPut: useRequestMutation<any, { due_date: string }>({
      mutationFnRequestProps: (data) => ({
        urlBackendPath: `/communities/${classId}/admin/assignments/${assignmentId}`,
        method: "PUT",
        body: JSON.stringify(data),
      }),
      onSuccess: () => invalidateQueries(),
    }),
  };
};
